import React, { useEffect, useState } from "react";
import { AdminUserForm } from "../Forms/AdminUserForm";
import { useNavigate } from "react-router-dom";
import { Toast } from "../../../components/Common/Toast";
import { NoRecordFound } from "../../../components/Common/NoRecordFound";
import PaginationButtons from "../../../components/Common/Pagination";
import Dialogs from "../../../components/Common/Dialog";
import { UserModel } from "../../../interfaces/user.interface";
import { TokenCookieManager } from "../../../utils/tokenCookies";
import { deleteUserService, getUserDetailsService, getUsersService } from "./services/Users.service";
import LoadingSpinner from "../../../components/LoadingSpinner/LoadingSpinner";
import UserRow from "./components/UserRow";
import AdminSearchInput from "../../../components/Admin/AdminSearchInput";
import AdminTableHeader from "../../../components/Admin/AdminTableHeader";
import TableInfoBar from "../../../components/Admin/TableInfoBar";

import { UserRoleEnum } from "../../../interfaces/user.interface";

const ADMIN_ROLE_OPTIONS = [
    { value: '', label: 'All Roles' },
    { value: UserRoleEnum.superAdmin, label: 'Super Admin' },
    { value: UserRoleEnum.admin, label: 'Admin' },
    { value: UserRoleEnum.customerService, label: 'Customer Service' },
];

const Users = () => {
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState<number | null>(null);
    const [totalRecords, setTotalRecords] = useState<number>(0);
    const [searchTerm, setSearchTerm] = useState("");
    const [roleFilter, setRoleFilter] = useState("");
    const [openDialog, setOpenDialog] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [users, setUsers] = useState<UserModel[]>([]);
    const [userId, setUserId] = useState<string | null>(null);
    const [userDetails, setUserDetails] = useState(null);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    useEffect(() => {
        if (!TokenCookieManager.hasToken()) {
            navigate("/admin");
        }
    });

    const getUsers = async (search?: string, role?: string) => {
        try {
            setLoading(true);
            const effectiveRole = role ?? roleFilter;
            const { users, totalPages, totalRecords: tr } = await getUsersService(currentPage, search, effectiveRole || undefined);
            setUsers(users);
            setTotalPages(totalPages);
            setTotalRecords(tr);
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = (term: string) => {
        setSearchTerm(term);
        setCurrentPage(1);
        getUsers(term || undefined);
    };

    const handleRoleChange = (role: string) => {
        setRoleFilter(role);
        setCurrentPage(1);
        getUsers(searchTerm || undefined, role);
    };

    const handleEdit = async (id: string) => {
        try {
            const userDetail = await getUserDetailsService(id);
            if (userDetail) {
                setShowForm(true);
                setUserDetails(userDetail);
            }
        } catch (error) {
            Toast("Error fetching user details", true);
        }
    };

    const handleDelete = async () => {
        try {
            await deleteUserService(userId!);
            Toast("User Deleted Successfully");
            getUsers(searchTerm || undefined);
            setOpenDialog(false);
        } catch (err) {
            Toast("User Delete Failed", true);
        }
    };

    useEffect(() => {
        getUsers(searchTerm || undefined);
    }, [currentPage]); // eslint-disable-line react-hooks/exhaustive-deps

    const handlePageChange = (page: string | number) => {
        const pageNumber = Number(page);
        if (pageNumber > 0 && pageNumber <= totalPages!) {
            setCurrentPage(pageNumber);
        }
    };

    const handleClose = () => {
        setOpenDialog(false);
    };

    return (
        <div className="p-0 md:p-4">
            {showForm ? (
                <AdminUserForm setShowForm={setShowForm} getUsers={getUsers} userDetails={userDetails} />
            ) : (
                <>
                    {/* Filter Bar */}
                    <div className="flex flex-wrap gap-3 items-end mb-4">
                        {/* Search (left) */}
                        <AdminSearchInput
                            placeholder="Search name or email..."
                            onSearch={handleSearch}
                            className="w-56"
                        />
                        {/* Role */}
                        <div className="flex flex-col">
                            <label className="text-xs text-gray-500 font-semibold mb-1">Role</label>
                            <select
                                value={roleFilter}
                                onChange={(e) => handleRoleChange(e.target.value)}
                                className="border border-gray-300 rounded px-2 py-1.5 text-sm bg-white"
                            >
                                {ADMIN_ROLE_OPTIONS.map((opt) => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </select>
                        </div>
                        {/* New User (right) */}
                        <div className="ml-auto">
                            <button
                                className="bg-storyblue text-white font-bold rounded-md text-sm px-4 py-1.5 hover:opacity-90"
                                onClick={() => {
                                    setShowForm(true);
                                    setUserDetails(null);
                                }}
                            >
                                + New User
                            </button>
                        </div>
                    </div>
                    {loading ? (
                        <LoadingSpinner />
                    ) : (
                        <div className="w-full overflow-x-auto">
                            <table className="min-w-full border border-gray-300">
                                <AdminTableHeader
                                    columns={[
                                        { key: 'name', label: 'Name' },
                                        { key: 'email', label: 'Email' },
                                        { key: 'role', label: 'Role' },
                                        { key: 'createdAt', label: 'Created At' },
                                        { key: 'actions', label: '' },
                                    ]}
                                />
                                <tbody>
                                    {users && users.length > 0 ? (
                                        users?.map((user, index) => (
                                            <UserRow
                                                key={user.id}
                                                user={user}
                                                handleEdit={handleEdit}
                                                setOpenDialog={setOpenDialog}
                                                setUserId={setUserId}
                                                index={index}
                                            />
                                        ))
                                    ) : (
                                        <NoRecordFound />
                                    )}
                                </tbody>
                            </table>
                            <div className="flex justify-between items-center mt-4">
                                <TableInfoBar
                                    currentPage={currentPage}
                                    pageSize={25}
                                    totalPages={totalPages || 0}
                                    totalRecords={totalRecords}
                                    recordsOnPage={users.length}
                                />
                                {(totalPages ?? 0) > 1 && (
                                    <PaginationButtons
                                        totalPages={totalPages!}
                                        currentPage={currentPage}
                                        handlePageChange={handlePageChange}
                                    />
                                )}
                            </div>
                        </div>
                    )}
                    <Dialogs openDialog={openDialog} handelClose={handleClose} handelProcess={handleDelete} />
                </>
            )}
        </div>
    );
};
export default Users;
